/**
 * Per-theme chart color defaults — the "factory" colors each Edge color profile
 * hands the chart before any user customization. Keyed by base MODE (the class
 * on <html>), so custom presets inherit their base mode's chart colors.
 *
 * Two families live here, both of which resist the plain `--chart-*` CSS-token
 * approach:
 *  - Candles: sourced as EXACT hex (the HSL token round-trip is lossy — see
 *    chart-theme.ts) and consumed by `buildChartTheme()`.
 *  - Session overlays (pre-market / after-hours shading, day break line): painted
 *    onto the <canvas>, where `ctx.fillStyle` cannot resolve `var(--x)`, so they
 *    must be concrete color strings resolved at apply time. Published through
 *    ChartThemeColorsContext by ChartThemeColorsProvider (themeVersion-reactive).
 *
 * Canvas background is deliberately NOT here: it is the theme-driven
 * `--chart-canvas` token in index.css.
 */
export type ChartThemeMode = 'light' | 'dark' | 'black'

export interface ChartColorDefaults {
  /** Candle up / down bodies (and the OHLC, last-price, volume tints). */
  candleUp: string
  candleDown: string
  /** Pre-market session shading fill, drawn behind the bars. Must be translucent. */
  preMarket: string
  /** After-hours session shading fill. Must be translucent. */
  afterHours: string
  /** New-trading-day vertical break line. `null` → this profile draws no line. */
  sessionBreak: string | null
}

const TABLE: Record<ChartThemeMode, ChartColorDefaults> = {
  dark: {
    candleUp: '#22c55e',
    candleDown: '#ef4444',
    // Translucent, never solid: an opaque band hides the grid behind it and, on
    // AG charts (History Price Action Graph), the drag-to-zoom highlight, which
    // AG draws UNDER crossline ranges. Over the dark canvas (#11141d) these land
    // on the same shades the old solid fills had (#212121 / #171c26).
    preMarket: 'rgba(255, 255, 255, 0.06)',
    afterHours: 'rgba(147, 197, 253, 0.045)',
    sessionBreak: '#204e79',
  },
  black: {
    candleUp: '#1c9c4b',
    candleDown: '#d33131',
    // Alpha is the band-visibility knob: the shading sits behind the bars, so
    // the grid still shows through. 0.07 (Chris, 2026-09-13): the bands should
    // be a faint tint of the pre-market / after-hours window, not a gray block.
    preMarket: 'rgba(147, 197, 253, 0.07)',
    afterHours: 'rgba(147, 197, 253, 0.07)',
    sessionBreak: 'rgba(147, 197, 253, 0.25)',
  },
  light: {
    candleUp: '#16a34a',
    candleDown: '#dc2626',
    // Raised from 0.15 / 0.25 so the bands are clearly visible on the white
    // canvas without going opaque (grid lines still read through).
    preMarket: 'rgba(253, 186, 116, 0.30)',
    afterHours: 'rgba(96, 165, 250, 0.40)',
    // Light profile ships no day break line by default.
    sessionBreak: null,
  },
}

/** Current chart mode from the <html> class list — black stays distinct from dark. */
export function currentChartThemeMode(): ChartThemeMode {
  const cl = document.documentElement.classList
  if (cl.contains('black')) return 'black'
  if (cl.contains('dark')) return 'dark'
  return 'light'
}

export function chartColorDefaultsFor(mode: ChartThemeMode): ChartColorDefaults {
  return TABLE[mode]
}

/** The active theme's chart color defaults, read live from the DOM. */
export function currentChartColorDefaults(): ChartColorDefaults {
  return TABLE[currentChartThemeMode()]
}
